import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Settings, Eye, Code, Trash2, GripVertical, Copy, ExternalLink,
  ChevronDown, ChevronUp, FileText, Webhook, BarChart3, CheckCircle2,
  XCircle, Clock, AlertTriangle, Send
} from 'lucide-react';
import api from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio' },
  { value: 'date', label: 'Date' },
  { value: 'hidden', label: 'Hidden' },
];

const HIDDEN_TRACKING_FIELDS = [
  'affiliate_id', 'referral_code', 'sub_ref', 'session_id',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content',
  'fbclid', 'gclid', 'landing_page', 'referrer',
  'form_slug', 'page_name', 'product_hint',
];

export default function FormBuilderPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('forms');
  const [selectedForm, setSelectedForm] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showEmbed, setShowEmbed] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [submissionsTotal, setSubmissionsTotal] = useState(0);

  const loadForms = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getLeadForms();
      setForms(data);
    } catch (e) {
      console.error('Failed to load forms:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadForms(); }, [loadForms]);

  const loadSubmissions = useCallback(async (formId) => {
    try {
      const data = await api.getFormSubmissions(formId);
      setSubmissions(data.submissions || []);
      setSubmissionsTotal(data.total || 0);
    } catch (e) { console.error(e); }
  }, []);

  const loadWebhookLogs = useCallback(async (formId) => {
    try {
      const data = await api.getWebhookDeliveries(formId);
      setWebhookLogs(data || []);
    } catch (e) { console.error(e); }
  }, []);

  return (
    <div className="space-y-6" data-testid="form-builder-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Lead Form Builder</h1>
          <p className="text-sm text-muted-foreground mt-1">Create forms, track submissions, manage webhooks</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-form-btn">
          <Plus className="h-4 w-4 mr-2" /> New Form
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="forms" data-testid="tab-forms"><FileText className="h-4 w-4 mr-1.5" /> Forms</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="tab-submissions"><BarChart3 className="h-4 w-4 mr-1.5" /> Submissions</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks"><Webhook className="h-4 w-4 mr-1.5" /> Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="forms">
          <FormsList
            forms={forms}
            loading={loading}
            onSelect={(f) => setSelectedForm(f)}
            onEmbed={(f) => setShowEmbed(f)}
            onRefresh={loadForms}
          />
        </TabsContent>

        <TabsContent value="submissions">
          <SubmissionsDashboard
            forms={forms}
            submissions={submissions}
            total={submissionsTotal}
            onLoadSubmissions={loadSubmissions}
          />
        </TabsContent>

        <TabsContent value="webhooks">
          <WebhookLogs
            forms={forms}
            logs={webhookLogs}
            onLoadLogs={loadWebhookLogs}
          />
        </TabsContent>
      </Tabs>

      {showCreate && (
        <CreateFormDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadForms(); }}
        />
      )}

      {selectedForm && (
        <FormEditorDialog
          form={selectedForm}
          open={!!selectedForm}
          onClose={() => setSelectedForm(null)}
          onSaved={() => { setSelectedForm(null); loadForms(); }}
        />
      )}

      {showEmbed && (
        <EmbedDialog
          form={showEmbed}
          open={!!showEmbed}
          onClose={() => setShowEmbed(null)}
        />
      )}
    </div>
  );
}

// ── Forms List ────────────────────────────────────────────────

function FormsList({ forms, loading, onSelect, onEmbed, onRefresh }) {
  const handleDelete = async (formId) => {
    if (!window.confirm('Archive this form?')) return;
    try {
      await api.deleteLeadForm(formId);
      onRefresh();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading forms...</div>;

  if (forms.length === 0) {
    return (
      <Card className="p-12 text-center">
        <FileText className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-semibold mb-2">No forms yet</h3>
        <p className="text-sm text-muted-foreground">Create your first lead capture form to start collecting leads.</p>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      {forms.filter(f => f.status !== 'archived').map(form => (
        <Card key={form.form_id} className="p-4" data-testid={`form-card-${form.form_id}`}>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold truncate">{form.name}</h3>
                <Badge variant={form.status === 'active' ? 'default' : 'secondary'}>
                  {form.status}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{form.description || 'No description'}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span>{form.fields?.length || 0} fields</span>
                <span>{form.submission_count || 0} submissions</span>
                <span>Slug: {form.slug}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Button variant="ghost" size="sm" onClick={() => onSelect(form)} data-testid={`edit-form-${form.form_id}`}>
                <Settings className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEmbed(form)} data-testid={`embed-form-${form.form_id}`}>
                <Code className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => window.open(`${BACKEND_URL}/api/forms/public/${form.slug}`, '_blank')}>
                <Eye className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(form.form_id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ── Create Form Dialog ────────────────────────────────────────

function CreateFormDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.createLeadForm({ name, description });
      onCreated();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="create-form-dialog">
        <DialogHeader>
          <DialogTitle>Create New Form</DialogTitle>
          <DialogDescription>Create a lead capture form with standard fields pre-loaded.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-sm font-medium">Form Name *</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Main Website Form" data-testid="form-name-input" />
          </div>
          <div>
            <label className="text-sm font-medium">Description</label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" />
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs font-medium mb-1">Pre-loaded Standard Fields:</p>
            <p className="text-xs text-muted-foreground">Business Name, Contact Name, Email, Phone, Address, City, State, ZIP, Business Type, Monthly Volume, Current Processor, Current POS</p>
            <p className="text-xs font-medium mt-2 mb-1">Auto-Injected Hidden Fields:</p>
            <p className="text-xs text-muted-foreground">{HIDDEN_TRACKING_FIELDS.join(', ')}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!name.trim() || saving} data-testid="confirm-create-form">
              {saving ? 'Creating...' : 'Create Form'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Form Editor Dialog ────────────────────────────────────────

function FormEditorDialog({ form, open, onClose, onSaved }) {
  const [fields, setFields] = useState(form.fields || []);
  const [settings, setSettings] = useState(form.settings || {});
  const [tab, setTab] = useState('fields');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateLeadForm(form.form_id, {
        fields: fields.map((f, i) => ({...f, order: i})),
        settings,
      });
      onSaved();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const addField = () => {
    setFields([...fields, {
      field_id: `custom_${Date.now()}`,
      field_type: 'text',
      label: 'New Field',
      name: `field_${Date.now()}`,
      required: false,
      order: fields.length,
    }]);
  };

  const updateField = (idx, updates) => {
    const updated = [...fields];
    updated[idx] = { ...updated[idx], ...updates };
    setFields(updated);
  };

  const removeField = (idx) => {
    setFields(fields.filter((_, i) => i !== idx));
  };

  const moveField = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= fields.length) return;
    const updated = [...fields];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setFields(updated);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[900px] w-[90vw] max-h-[85vh] overflow-y-auto" data-testid="form-editor-dialog">
        <DialogHeader>
          <DialogTitle>Edit: {form.name}</DialogTitle>
          <DialogDescription>Configure fields, settings, webhooks, and redirects.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="fields">Fields</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
            <TabsTrigger value="tracking">Tracking</TabsTrigger>
          </TabsList>

          <TabsContent value="fields" className="space-y-3">
            {fields.map((field, idx) => (
              <div key={field.field_id} className="flex items-start gap-3 p-3 border rounded-lg bg-card" data-testid={`field-row-${idx}`}>
                <div className="flex flex-col gap-1 pt-1">
                  <button onClick={() => moveField(idx, -1)} className="text-muted-foreground hover:text-foreground"><ChevronUp className="h-3 w-3" /></button>
                  <GripVertical className="h-3 w-3 text-muted-foreground" />
                  <button onClick={() => moveField(idx, 1)} className="text-muted-foreground hover:text-foreground"><ChevronDown className="h-3 w-3" /></button>
                </div>
                <div className="flex-1 grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-[11px] text-muted-foreground mb-0.5 block">Label</label>
                    <Input value={field.label} onChange={e => updateField(idx, { label: e.target.value })} placeholder="Label" className="text-sm" />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground mb-0.5 block">Field Name</label>
                    <Input value={field.name} onChange={e => updateField(idx, { name: e.target.value })} placeholder="Field name" className="text-sm" />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground mb-0.5 block">Type</label>
                    <Select value={field.field_type} onValueChange={v => updateField(idx, { field_type: v })}>
                      <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground mb-0.5 block">Placeholder</label>
                    <Input value={field.placeholder || ''} onChange={e => updateField(idx, { placeholder: e.target.value })} placeholder="Placeholder" className="text-sm" />
                  </div>
                  {(field.field_type === 'dropdown' || field.field_type === 'radio') && (
                    <div className="col-span-4">
                      <label className="text-[11px] text-muted-foreground mb-0.5 block">Options (comma-separated)</label>
                      <Input
                        className="text-sm"
                        value={(field.options || []).join(', ')}
                        onChange={e => updateField(idx, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        placeholder="Option 1, Option 2, Option 3"
                      />
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-center gap-2 pt-5">
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={field.required} onChange={e => updateField(idx, { required: e.target.checked })} className="rounded" />
                    Req
                  </label>
                  <button onClick={() => removeField(idx)} className="text-destructive hover:text-destructive/80">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addField} className="w-full" data-testid="add-field-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Field
            </Button>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <div>
              <label className="text-sm font-medium">Success Message</label>
              <Input
                value={settings.success_message || ''}
                onChange={e => setSettings({...settings, success_message: e.target.value})}
                placeholder="Thank you message after submission"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Redirect URL (after submit)</label>
              <Input
                value={settings.redirect_url || ''}
                onChange={e => setSettings({...settings, redirect_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">Assignment Rule</label>
              <Select value={settings.assignment_rule || 'round_robin'} onValueChange={v => setSettings({...settings, assignment_rule: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="round_robin">Round Robin</SelectItem>
                  <SelectItem value="specific">Specific Rep</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {settings.assignment_rule === 'specific' && (
              <div>
                <label className="text-sm font-medium">Assigned User ID</label>
                <Input
                  value={settings.assigned_user_id || ''}
                  onChange={e => setSettings({...settings, assigned_user_id: e.target.value})}
                  placeholder="User ID"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Tags (comma-separated)</label>
              <Input
                value={(settings.tags || []).join(', ')}
                onChange={e => setSettings({...settings, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                placeholder="vip, restaurant, clover"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium">GHL Sync</label>
                <p className="text-xs text-muted-foreground">Send leads to GoHighLevel</p>
              </div>
              <Switch checked={settings.ghl_enabled || false} onCheckedChange={v => setSettings({...settings, ghl_enabled: v})} />
            </div>
          </TabsContent>

          <TabsContent value="webhooks" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium">Enable Webhook</label>
                <p className="text-xs text-muted-foreground">POST data to an external URL on each submission</p>
              </div>
              <Switch
                checked={settings.webhook_enabled || false}
                onCheckedChange={v => setSettings({...settings, webhook_enabled: v})}
                data-testid="webhook-toggle"
              />
            </div>
            {settings.webhook_enabled && (
              <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                <div>
                  <label className="text-sm font-medium">Webhook URL *</label>
                  <Input
                    value={settings.webhook_config?.url || ''}
                    onChange={e => setSettings({...settings, webhook_config: {...(settings.webhook_config || {}), url: e.target.value, enabled: true}})}
                    placeholder="https://hooks.example.com/lead"
                    data-testid="webhook-url-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">API Key</label>
                    <Input
                      value={settings.webhook_config?.api_key || ''}
                      onChange={e => setSettings({...settings, webhook_config: {...(settings.webhook_config || {}), api_key: e.target.value}})}
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">API Secret (HMAC)</label>
                    <Input
                      type="password"
                      value={settings.webhook_config?.api_secret || ''}
                      onChange={e => setSettings({...settings, webhook_config: {...(settings.webhook_config || {}), api_secret: e.target.value}})}
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Webhooks retry 3 times with exponential backoff. Payload is signed with HMAC-SHA256 if a secret is provided.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="tracking" className="space-y-4">
            <Card className="p-4">
              <h4 className="text-sm font-semibold mb-2">Auto-Injected Hidden Tracking Fields</h4>
              <p className="text-xs text-muted-foreground mb-3">These fields are automatically captured from URL parameters and cookies on every submission.</p>
              <div className="flex flex-wrap gap-2">
                {HIDDEN_TRACKING_FIELDS.map(f => (
                  <Badge key={f} variant="outline" className="text-xs">{f}</Badge>
                ))}
              </div>
            </Card>
            <Card className="p-4">
              <h4 className="text-sm font-semibold mb-2">Attribution Model</h4>
              <p className="text-xs text-muted-foreground">V1 uses <strong>FIRST TOUCH</strong> attribution. The original referral source is preserved on duplicate leads. Admin override is available.</p>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-form-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Embed Code Dialog ─────────────────────────────────────────

function EmbedDialog({ form, open, onClose }) {
  const publicUrl = `${BACKEND_URL}/forms/${form.slug}`;
  const iframeCode = `<iframe src="${publicUrl}" width="100%" height="800" frameborder="0" style="border:none;"></iframe>`;
  const directApiUrl = `${BACKEND_URL}/api/leads/capture`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="embed-dialog">
        <DialogHeader>
          <DialogTitle>Embed & Share: {form.name}</DialogTitle>
          <DialogDescription>Use these codes to embed the form on your website.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              <ExternalLink className="h-4 w-4" /> Public URL
            </label>
            <div className="flex gap-2 mt-1">
              <Input value={publicUrl} readOnly className="text-xs font-mono" data-testid="public-url" />
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(publicUrl)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              <Code className="h-4 w-4" /> Iframe Embed
            </label>
            <div className="flex gap-2 mt-1">
              <Input value={iframeCode} readOnly className="text-xs font-mono" />
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(iframeCode)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              <Send className="h-4 w-4" /> API Endpoint
            </label>
            <div className="mt-1 p-3 bg-muted/50 rounded-lg font-mono text-xs">
              <p className="text-muted-foreground mb-1">POST {directApiUrl}</p>
              <p className="text-muted-foreground">Content-Type: application/json</p>
              <p className="text-muted-foreground mt-1">Include <code>form_slug: "{form.slug}"</code> in payload</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Submissions Dashboard ─────────────────────────────────────

function SubmissionsDashboard({ forms, submissions, total, onLoadSubmissions }) {
  const [selectedFormId, setSelectedFormId] = useState('');

  useEffect(() => {
    onLoadSubmissions(selectedFormId || null);
  }, [selectedFormId, onLoadSubmissions]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={selectedFormId || "all"} onValueChange={(v) => setSelectedFormId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64" data-testid="submission-form-filter">
            <SelectValue placeholder="All Forms" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Forms</SelectItem>
            {forms.map(f => <SelectItem key={f.form_id} value={f.form_id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{total} total submissions</span>
      </div>

      {submissions.length === 0 ? (
        <Card className="p-8 text-center">
          <BarChart3 className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">No submissions yet</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {submissions.map(sub => (
            <Card key={sub.submission_id} className="p-4" data-testid={`submission-${sub.submission_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{sub.raw_payload?.business_name || sub.raw_payload?.contact_name || 'Unknown'}</span>
                    <Badge variant={sub.is_new_lead ? 'default' : 'secondary'}>
                      {sub.is_new_lead ? 'New Lead' : 'Updated'}
                    </Badge>
                    {sub.product_routing?.product && (
                      <Badge variant="outline">{sub.product_routing.product}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{sub.raw_payload?.email}</span>
                    <span>{sub.raw_payload?.phone}</span>
                    {sub.tracking_data?.affiliate_id && <span>Ref: {sub.tracking_data.affiliate_id}</span>}
                    {sub.tracking_data?.utm_source && <span>UTM: {sub.tracking_data.utm_source}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <WebhookStatusBadge status={sub.webhook_status} />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(sub.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Webhook Logs ──────────────────────────────────────────────

function WebhookLogs({ forms, logs, onLoadLogs }) {
  const [selectedFormId, setSelectedFormId] = useState('');

  useEffect(() => {
    onLoadLogs(selectedFormId || null);
  }, [selectedFormId, onLoadLogs]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={selectedFormId || "all"} onValueChange={(v) => setSelectedFormId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="All Forms" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Forms</SelectItem>
            {forms.map(f => <SelectItem key={f.form_id} value={f.form_id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {logs.length === 0 ? (
        <Card className="p-8 text-center">
          <Webhook className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">No webhook deliveries yet</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {logs.map((log, idx) => (
            <Card key={idx} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <WebhookStatusBadge status={log.status} />
                    <span className="text-sm font-mono truncate">{log.webhook_url}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>Attempts: {log.attempts}/{log.max_retries}</span>
                    {log.response_status && <span>HTTP {log.response_status}</span>}
                    {log.error && <span className="text-destructive">{log.error}</span>}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {log.last_attempt_at ? new Date(log.last_attempt_at).toLocaleString() : '-'}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function WebhookStatusBadge({ status }) {
  const config = {
    delivered: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', label: 'Delivered' },
    pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Pending' },
    retrying: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50', label: 'Retrying' },
    failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Failed' },
    none: { icon: null, color: 'text-muted-foreground', bg: 'bg-muted/50', label: '-' },
  };
  const c = config[status] || config.none;
  if (!c.icon) return null;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${c.bg} ${c.color}`}>
      <Icon className="h-3 w-3" /> {c.label}
    </span>
  );
}
